import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import { FLEET_DEFAULT_DAYS, parseFleetActivationFilters, useFleetOperatorNotes } from "@visioni/a3s-monitoring/app";
import { activationHref, fleetClientHref } from "../routes";
import { useListTimeZone } from "../hooks/useListTimeZone";
import { ClientNumberFilter, MatchedClients, NO_MATCHED_CLIENTS } from "../components/ClientNumberFilter";
import { OperatorNotesTable } from "../components/OperatorNotesTable";
import { OrderField } from "../components/OrderField";
import { TimeBoundFields } from "../components/TimeBoundFields";
import { TimeZoneField } from "../components/TimeZoneField";
import { QueryNotice } from "../components/QueryNotice";

const blank = (value: string | undefined) => (value === "" ? undefined : value);
// The fleet list has no site, so Default is always the viewer's own zone.
const noSiteTimeZone = () => undefined;

/**
 * Every operator note in the replica, across clients, filtered by the fleet
 * activations' time range (`?tz=&from=&to=&order=`) and by the start of a
 * client number (`?clientNo=`) — an asset number narrows it to that asset's
 * clients, which the page names.
 *
 * Ticketless notes are listed too: autodial call logs are written against the
 * client alone, so no activation shows them.
 */
export function FleetOperatorNotesScreen() {
  const router = useRouter();
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const params = useLocalSearchParams<{ from?: string; to?: string; tz?: string; order?: string; clientNo?: string }>();
  const from = blank(params.from);
  const to = blank(params.to);
  const order = blank(params.order);
  const clientNo = blank(params.clientNo);
  const { timeZone, fallback } = useListTimeZone({
    tz: blank(params.tz),
    site: undefined,
    siteTimeZone: noSiteTimeZone,
    onResolve: (tz) => router.setParams({ tz }),
  });
  const parsed =
    timeZone === undefined ? undefined : (
      parseFleetActivationFilters({ from, to, tz: timeZone, order, clientNo }, new Date())
    );

  const list = useFleetOperatorNotes({
    filters: parsed?.ok === true ? parsed.filters : { since: "" },
    enabled: parsed?.ok === true,
  });

  return (
    <View style={styles.page}>
      {timeZone !== undefined && (
        <View style={styles.filters}>
          <View style={styles.bounds}>
            <TimeBoundFields
              value={{ from, to }}
              onChange={(next) => router.setParams({ from: next.from, to: next.to })}
              fromPlaceholder={`Last ${String(FLEET_DEFAULT_DAYS)} days`}
              leading={
                <TimeZoneField
                  hasSiteFilter={false}
                  value={timeZone}
                  fallback={fallback}
                  at={parsed?.ok === true ? new Date(parsed.filters.since) : new Date()}
                  onChange={(tz) => router.setParams({ tz })}
                />
              }
            />
          </View>
          <View style={styles.narrowing}>
            <View style={styles.order}>
              <OrderField value={order} onChange={(next) => router.setParams({ order: next })} />
            </View>
            <ClientNumberFilter
              clientNo={clientNo}
              onChange={(next) => router.setParams({ clientNo: next })}
              rows="notes"
            />
          </View>
          <MatchedClients rows="Notes" clientNos={list.clientNos} />
        </View>
      )}
      {parsed === undefined || timeZone === undefined ?
        <View style={styles.notice}>
          <QueryNotice isLoading error={null} onRetry={list.refetch} />
        </View>
      : !parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice
            isLoading={list.isLoading}
            error={list.error}
            onRetry={list.refetch}
            errorTitle="Could not load notes"
          />
        </View>
      : list.clientNos?.length === 0 ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            {NO_MATCHED_CLIENTS}
          </Text>
        </View>
      : list.items.length === 0 && list.nextCursor === null && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No notes found
          </Text>
        </View>
      : <OperatorNotesTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          clientHref={(no) => fleetClientHref(tenantId, no)}
          // A note names no tenant, so it opens the fleet page, which serves any ticket.
          activationHref={(ticketNo) => activationHref(tenantId, ticketNo, undefined)}
          timeZone={timeZone}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filters: {
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the pickers open over the rows.
    zIndex: 1,
  },
  // Above the fields below it, so the timezone list and calendars open over them.
  bounds: { zIndex: 1 },
  narrowing: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: theme.spacing.lg },
  // The width of the timezone field above it.
  order: { flexBasis: "24%", maxWidth: 420, minWidth: 220 },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
